import {
  IonCard,
  IonCardContent,
  IonText,
  IonLabel,
  IonList,
  IonItem,
  useIonAlert,
  IonCardHeader,
  IonCardTitle,
  IonCheckbox,
} from "@ionic/react";
import { useContext } from "react";
import {
  TranslationsContext,
  TranslationMessagesContext,
} from "../util/Translations";
import { useHistory } from "react-router";
import { useOfflineStore } from "../store/offline-mode";
import { useEquipmentStore } from "../store/equipment";
import { useShallow } from "zustand/react/shallow";

export interface Inspection {
  id: string;
  name: string;
  required: boolean;
  status?: "Completed" | "Not Complete" | "Failed";
  group: "Pre Job" | "Inspection" | "Post Job";
  inspectionId: number;
}

interface Props {
  title: string;
  inspections: Inspection[];
  workOrderId?: string;
  inspectionAllowed?: boolean;
  woMaxStatus?: string;
  onInspectionClick?: (inspection: Inspection) => void;
  isEquipmentContext?: boolean;
  equipmentId?: string;
  isSelectable?: boolean;
}

const InspectionGroupList: React.FC<Props> = ({
  title,
  inspections,
  workOrderId,
  inspectionAllowed = true,
  woMaxStatus = "",
  onInspectionClick,
  isEquipmentContext = false,
  equipmentId,
  isSelectable,
}) => {
  const isOffline = useOfflineStore((state) => state.isOffline);
  const selectedInspection = useEquipmentStore(
    useShallow((state) => state.selectedInspection)
  );
  const history = useHistory();
  const { translations } = useContext(TranslationsContext);
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const [presentAlert] = useIonAlert();
  const toggleSelectedInspection = useEquipmentStore(
    (state) => state.toggleSelectedInspection
  );

  const handleInspectionClick = (inspection: Inspection) => {
    if (onInspectionClick) {
      onInspectionClick(inspection);
      return;
    }
    if (isEquipmentContext) {
      if (!equipmentId) {
        return;
      }
      const encodedEq = encodeURIComponent(equipmentId);
      const route = `/Equipment/${encodedEq}/inspection/${inspection.id}`;
      history.push(route);
      return;
    }

    if (!workOrderId) {
      return;
    }

    if (!inspectionAllowed) {
      presentAlert({
        header: translatedMessages["WOApprovalNeeded"]?.MessageText.replace(
          "@1",
          woMaxStatus
        ),
        buttons: [{ text: translations["lbl_btn_ok"] || "OK" }],
      });
      return;
    }

    const encodedWo = encodeURIComponent(workOrderId);
    const isCompleted = inspection.status === "Completed";
    const route = `/workorder/${encodedWo}/inspection/${inspection.id}${
      isCompleted ? "?readonly" : ""
    }`;
    history.push(route);
  };

  return (
    <>
      <IonLabel className="ion-margin-start ion-text-uppercase">
        <strong>{title}</strong>
      </IonLabel>
      <IonList lines="none">
        {inspections.map((inspection) => (
          <IonCard
            key={inspection.id}
            button
            onClick={() => handleInspectionClick(inspection)}
          >
            <IonCardHeader>
              <IonCardTitle>
                {isOffline && isSelectable && (
                  <IonCheckbox
                    checked={selectedInspection.some(
                      (i) =>
                        i.id.toString() === inspection.id.toString() &&
                        i.equipmentId?.toString() === equipmentId?.toString()
                    )}
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      toggleSelectedInspection({ ...inspection, equipmentId });
                    }}
                    className="ion-margin-end"
                  />
                )}
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonItem lines="none">
                <IonLabel>
                  <IonText>
                    {translations["lbl_inspection_id"] || "Inspection ID"}:{" "}
                    {inspection.id}
                  </IonText>
                </IonLabel>
              </IonItem>
              <IonItem lines="none">
                <IonLabel>
                  <IonText>
                    {translations["lbl_name"] || "Name"}:
                    <IonText color={inspection.required ? "danger" : "dark"}>
                      {" "}
                      {inspection.name}
                    </IonText>
                  </IonText>
                </IonLabel>
              </IonItem>
              {!isEquipmentContext && (
                <IonItem lines="none">
                  <IonLabel>
                    <IonText>
                      {translations["lbl_status"] || "Status"}:
                      <IonText
                        color={
                          inspection.status === "Completed"
                            ? "success"
                            : inspection.status === "Failed"
                            ? "danger"
                            : "warning"
                        }
                      >
                        {" "}
                        {inspection.status}
                      </IonText>
                    </IonText>
                  </IonLabel>
                </IonItem>
              )}
            </IonCardContent>
          </IonCard>
        ))}
      </IonList>
    </>
  );
};

export default InspectionGroupList;
